import React, { useState } from 'react';
import { Search, X } from './Icons';
import './ActivitySelector.css';

const ActivitySelector = ({ onQuery, loading, onClearResults, bookmarks }) => {
  const [userId, setUserId] = useState('');
  const [selectedUids, setSelectedUids] = useState([]); // multi-selected bookmarked riders (OR)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [limit, setLimit] = useState(25);
  const [activityIds, setActivityIds] = useState('');
  const [mode, setMode] = useState('query'); // 'query' or 'id_lookup'

  const bookmarkedRiders = Object.entries(bookmarks?.riders || {})
    .filter(([, r]) => r.alias || r.flagged || r.note);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (mode === 'id_lookup') {
      const ids = activityIds.split(',').map(id => id.trim()).filter(id => id);
      if (ids.length === 0) return;
      onQuery({
        by: 'id_list',
        activityIds: JSON.stringify(ids),
      });
      return;
    }

    // Combine the manually-typed UID with any multi-selected bookmarked riders,
    // deduped. 1 rider -> userId ('=='); 2+ riders -> userIds OR query ('in'),
    // limit applied across the merged set by recency.
    const uids = [...new Set([
      ...selectedUids,
      ...(userId.trim() ? [userId.trim()] : []),
    ])];

    const params = {
      by: uids.length ? 'user' : 'recent',
      limit: parseInt(limit),
      orderDesc: sortOrder === 'desc',
    };

    if (uids.length === 1) params.userId = uids[0];
    else if (uids.length > 1) params.userIds = JSON.stringify(uids);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    onQuery(params);
  };

  const handleRiderClick = (uid) => {
    setMode('query');
    setSelectedUids(prev =>
      prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]
    );
  };

  const handleClearFilters = () => {
    setUserId('');
    setSelectedUids([]);
    setStartDate('');
    setEndDate('');
    setSortOrder('desc');
    setLimit(25);
    setActivityIds('');
  };

  const hasFilters = userId || selectedUids.length || startDate || endDate || sortOrder !== 'desc' || limit !== 25;

  return (
    <div className="activity-selector">
      <div className="selector-header">
        <h2><Search size={14} /> Query Activities</h2>
        <div className="mode-tabs">
          <button
            className={`mode-tab ${mode === 'query' ? 'active' : ''}`}
            onClick={() => setMode('query')}
            type="button"
          >
            Search
          </button>
          <button
            className={`mode-tab ${mode === 'id_lookup' ? 'active' : ''}`}
            onClick={() => setMode('id_lookup')}
            type="button"
          >
            ID Lookup
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {mode === 'query' ? (
          <div className="query-filters">
            <div className="filter-section">
              <div className="filter-row">
                <div className="form-group flex-1">
                  <label htmlFor="user-id">User ID</label>
                  <input
                    id="user-id"
                    name="userId"
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="All users"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="filter-row">
                <div className="form-group flex-1">
                  <label htmlFor="start-date">From</label>
                  <input
                    id="start-date"
                    name="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="form-group flex-1">
                  <label htmlFor="end-date">To</label>
                  <input
                    id="end-date"
                    name="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="filter-row">
                <div className="form-group flex-1">
                  <label htmlFor="sort-order">Sort</label>
                  <select
                    id="sort-order"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    disabled={loading}
                  >
                    <option value="desc">Newest first</option>
                    <option value="asc">Oldest first</option>
                  </select>
                </div>
                <div className="form-group limit-group">
                  <label htmlFor="limit">Limit</label>
                  <input
                    id="limit"
                    name="limit"
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    min="1"
                    max="200"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {hasFilters && (
              <button type="button" className="clear-filters-btn" onClick={handleClearFilters}>
                <X size={12} /> Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="query-filters">
            <div className="form-group">
              <label htmlFor="activity-ids">Activity IDs</label>
              <textarea
                id="activity-ids"
                name="activityIds"
                value={activityIds}
                onChange={(e) => setActivityIds(e.target.value)}
                placeholder="Comma-separated IDs, e.g. abc123, def456"
                rows="3"
                disabled={loading}
                required
              />
            </div>
          </div>
        )}

        <div className="query-actions">
          <button
            type="submit"
            className="query-button"
            disabled={loading || (mode === 'id_lookup' && !activityIds.trim())}
          >
            <Search size={14} />
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button
            type="button"
            className="clear-results-btn"
            onClick={onClearResults}
            disabled={loading}
          >
            Clear Results
          </button>
        </div>
      </form>

      {bookmarkedRiders.length > 0 && mode === 'query' && (
        <div className="bookmarked-riders">
          <h3>Bookmarked Riders {selectedUids.length > 1 && <span className="rider-or-count">· {selectedUids.length} selected (OR)</span>}</h3>
          <div className="rider-chips">
            {bookmarkedRiders.map(([uid, rider]) => (
              <button
                key={uid}
                className={`rider-chip ${rider.flagged ? 'rider-chip-flagged' : ''} ${selectedUids.includes(uid) ? 'rider-chip-active' : ''}`}
                title={`${uid}${rider.note ? '\n' + rider.note : ''}`}
                disabled={loading}
                onClick={() => handleRiderClick(uid)}
                type="button"
              >
                {rider.flagged && <span className="rider-chip-flag">⚑</span>}
                {rider.alias || uid.slice(0, 10) + '…'}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitySelector;
